import { TooltipDirective } from '../tooltip/tooltip.directive';
import { BOTONES } from '../../componentes/botones';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  NgZone,
  inject,
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { MAPBOX_TOKEN } from '../../../environments/environment';
import { HelperService } from '../servicios/helper.service';
import { aplicarEtiquetasMapa } from '../mapbox/etiquetas-mapa';

interface Circle {
  center?: [number, number];
  radius?: number;
}

/**
 * CircleEditorMapboxComponent
 *
 * Componente para editar círculos (radios de cobertura) usando Mapbox GL.
 * Reemplazo de PolygonEditorOlComponent específicamente para círculos.
 *
 * Implementación:
 * - Usa Mapbox GL Draw para dibujar polígonos
 * - Aproxima círculos como polígonos regulares de 64 lados
 * - Calcula centro y radio desde polígono dibujado
 *
 * API compatible con PolygonEditorOlComponent:
 * - Input: [circle] (center, radius)
 * - Output: (circleChange)
 * - Two-way binding: [(circle)]
 */
@Component({
  selector: 'app-circle-editor-mapbox',
  standalone: true,
  imports: [TooltipDirective, BOTONES, MatIconModule],
  templateUrl: './circle-editor-mapbox.component.html',
  styleUrl: './circle-editor-mapbox.component.scss',
})
export class CircleEditorMapboxComponent
  implements OnInit, OnDestroy, OnChanges
{
  private helper = inject(HelperService);
  private ngZone = inject(NgZone);

  @ViewChild('mapContainer', { static: true }) mapContainerRef!: ElementRef;

  @Input() circle?: Circle;
  @Output() circleChange = new EventEmitter<Circle>();

  @Input() color?: string;
  @Input() centrarA?: { coordinates: [number, number] };

  private map?: mapboxgl.Map;
  private draw?: MapboxDraw;
  public dibujando = false;
  private drawingCircle = false;
  public tempCircleCenter?: [number, number];
  private tempMarker?: mapboxgl.Marker;

  // Radio en metros por defecto (500m)
  private readonly DEFAULT_RADIUS = 500;

  ngOnInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    // Limpiar listeners antes de destruir el mapa
    this.removeCircleDrawListeners();

    if (this.tempMarker) {
      this.tempMarker.remove();
    }

    this.map?.remove();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['centrarA']?.currentValue && this.map) {
      this.panTo(this.centrarA!.coordinates);
    }

    if (changes['circle'] && this.map && this.draw) {
      const raw = changes['circle'].currentValue as any;
      // Normalizar: acepta { center, radius } o { coordinates, radius } (IGeoJSONCircle)
      const newCircle: Circle | undefined = raw
        ? { center: raw.center ?? raw.coordinates, radius: raw.radius }
        : undefined;

      // Limpiar dibujos previos
      this.draw.deleteAll();

      if (newCircle?.center && newCircle?.radius) {
        // Dibujar círculo desde input
        this.drawCircleFromInput(newCircle);
      }
    }

    if (changes['color'] && !changes['color'].firstChange && this.map) {
      this.updateDrawStyles();
    }
  }

  /**
   * Inicializa el mapa Mapbox GL
   */
  private initMap(): void {
    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Obtener posición inicial (centro de Argentina por defecto)
    const defaultCenter: [number, number] = [-58.3816, -34.6037]; // Buenos Aires
    const initialCenter =
      this.centrarA?.coordinates || this.circle?.center || defaultCenter;

    // CRÍTICO: Crear mapa FUERA de Angular Zone para evitar que Mapbox dispare change detection
    // El loop de renderizado interno de Mapbox usa requestAnimationFrame, MessageChannel y Promises
    // que quedarían patcheados por Zone.js si se crean dentro de Angular Zone
    this.ngZone.runOutsideAngular(() => {
      this.map = new mapboxgl.Map({
        language: 'es', // Etiquetas del mapa base en español
        container: this.mapContainerRef.nativeElement,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: initialCenter,
        zoom: this.circle?.center ? 14 : 12,
      });
      aplicarEtiquetasMapa(this.map);

      // Agregar controles de navegación
      this.map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: true,
        }),
        'bottom-right',
      );

      // Inicializar Mapbox Draw
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {},
        styles: this.getDrawStyles(),
      });

      this.map.addControl(this.draw);

      // Escuchar eventos de dibujo
      this.map.on('draw.create', this.onDrawCreate.bind(this));
      this.map.on('draw.update', this.onDrawUpdate.bind(this));

      // Dibujar círculo inicial si existe
      this.map.on('load', () => {
        const raw = this.circle as any;
        const center = raw?.center ?? raw?.coordinates;
        if (center && raw?.radius) {
          this.drawCircleFromInput({ center, radius: raw.radius });
        }
      });
    });
  }

  /**
   * Estilos personalizados para Mapbox Draw
   */
  private getDrawStyles(): any[] {
    const fillColor = this.color
      ? HelperService.hexToRgba(this.color, 0.2)
      : 'rgba(255, 204, 51, 0.2)';

    const strokeColor = this.color || '#ffcc33';

    return [
      // Polígono activo (dibujando)
      {
        id: 'gl-draw-polygon-fill-active',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon'], ['==', 'active', 'true']],
        paint: {
          'fill-color': fillColor,
          'fill-outline-color': strokeColor,
          'fill-opacity': 0.3,
        },
      },
      // Polígono inactivo (dibujado)
      {
        id: 'gl-draw-polygon-fill-inactive',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon'], ['==', 'active', 'false']],
        paint: {
          'fill-color': fillColor,
          'fill-outline-color': strokeColor,
          'fill-opacity': 0.3,
        },
      },
      // Bordes del polígono
      {
        id: 'gl-draw-polygon-stroke',
        type: 'line',
        filter: ['all', ['==', '$type', 'Polygon']],
        paint: {
          'line-color': strokeColor,
          'line-width': 2,
        },
      },
      // Vértices (puntos de edición)
      {
        id: 'gl-draw-polygon-vertex',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
        paint: {
          'circle-radius': 5,
          'circle-color': strokeColor,
        },
      },
      // Punto medio (para agregar vértices)
      {
        id: 'gl-draw-polygon-midpoint',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point']],
        paint: {
          'circle-radius': 3,
          'circle-color': strokeColor,
        },
      },
    ];
  }

  /**
   * Actualiza los estilos cuando cambia el color
   */
  private updateDrawStyles(): void {
    if (!this.draw || !this.map) return;

    // Recrear el control con nuevos estilos
    this.map.removeControl(this.draw);

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      styles: this.getDrawStyles(),
    });

    this.map.addControl(this.draw);

    // Re-dibujar círculo si existe
    if (this.circle?.center && this.circle?.radius) {
      this.drawCircleFromInput(this.circle);
    }
  }

  /**
   * Handler cuando se crea una geometría
   */
  private onDrawCreate(e: any): void {
    const feature = e.features[0];
    if (!feature) return;

    const circle = this.polygonToCircle(feature.geometry);
    this.circle = circle;
    this.circleChange.emit(circle);
    this.dibujando = false;
  }

  /**
   * Handler cuando se actualiza una geometría (editando vértices)
   */
  private onDrawUpdate(e: any): void {
    const feature = e.features[0];
    if (!feature) return;

    const circle = this.polygonToCircle(feature.geometry);
    this.circle = circle;
    this.circleChange.emit(circle);
  }

  /**
   * Inicia el modo de dibujo de círculo mejorado
   * En lugar de dibujar polígonos, usa un sistema de click-drag más intuitivo
   */
  public dibujar(): void {
    if (!this.draw || this.dibujando) return;

    this.dibujando = true;
    this.drawingCircle = true;

    // Limpiar dibujos previos
    this.draw.deleteAll();

    // Cambiar cursor
    if (this.map) {
      this.map.getCanvas().style.cursor = 'crosshair';
    }

    // Agregar listeners para el dibujo del círculo
    this.addCircleDrawListeners();
  }

  /**
   * Para el modo de dibujo
   */
  public parar(): void {
    if (!this.draw) return;

    this.dibujando = false;
    this.drawingCircle = false;
    this.tempCircleCenter = undefined;

    if (this.tempMarker) {
      this.tempMarker.remove();
      this.tempMarker = undefined;
    }

    if (this.map) {
      this.map.getCanvas().style.cursor = '';
      // Remover listeners
      this.removeCircleDrawListeners();
    }

    this.draw.changeMode('simple_select');
  }

  /**
   * Borra el círculo actual
   */
  public borrar(): void {
    if (!this.draw) return;

    this.draw.deleteAll();
    this.circle = undefined;
    this.circleChange.emit(undefined as any);
    this.dibujando = false;
    this.drawingCircle = false;
    this.tempCircleCenter = undefined;

    if (this.tempMarker) {
      this.tempMarker.remove();
      this.tempMarker = undefined;
    }

    if (this.map) {
      this.map.getCanvas().style.cursor = '';
      this.removeCircleDrawListeners();
    }
  }

  /**
   * Centra el mapa en unas coordenadas
   */
  private panTo(coordinates: [number, number]): void {
    if (!this.map || !coordinates) return;

    this.map.flyTo({
      center: coordinates,
      zoom: 16,
      duration: 1000,
    });
  }

  /**
   * Dibuja un círculo desde el input
   */
  private drawCircleFromInput(circle: Circle): void {
    if (!this.draw || !circle.center || !circle.radius) return;

    const polygon = this.circleToPolygon(circle.center, circle.radius);

    // Agregar feature al mapa
    const feature = {
      type: 'Feature' as const,
      properties: {},
      geometry: polygon,
    };

    this.draw.add(feature);

    // Centrar mapa en el círculo
    this.map?.flyTo({
      center: circle.center,
      zoom: 14,
    });
  }

  /**
   * Convierte un círculo (centro + radio) a un polígono regular de 64 lados
   *
   * @param center - [lng, lat]
   * @param radiusInMeters - Radio en metros
   * @returns GeoJSON Polygon
   */
  private circleToPolygon(
    center: [number, number],
    radiusInMeters: number,
  ): GeoJSON.Polygon {
    const points = 64; // Número de puntos (más = más suave)
    const coords: [number, number][] = [];

    // Convertir radio de metros a grados
    // 1 grado de latitud ≈ 111.32 km
    // 1 grado de longitud ≈ 111.32 km * cos(lat)
    const lat = center[1];
    const lng = center[0];

    const latOffset = radiusInMeters / (111.32 * 1000);
    const lngOffset =
      radiusInMeters / (111.32 * 1000 * Math.cos((lat * Math.PI) / 180));

    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const x = lng + lngOffset * Math.cos(angle);
      const y = lat + latOffset * Math.sin(angle);
      coords.push([x, y]);
    }

    // Cerrar el polígono (último punto = primer punto)
    coords.push(coords[0]);

    return {
      type: 'Polygon',
      coordinates: [coords],
    };
  }

  /**
   * Convierte un polígono a un círculo (centro + radio)
   * Calcula el centroide como centro y el radio promedio
   *
   * @param polygon - GeoJSON Polygon
   * @returns Circle { center, radius }
   */
  private polygonToCircle(polygon: GeoJSON.Polygon): Circle {
    const coords = polygon.coordinates[0];
    if (coords.length < 4) {
      // Polígono inválido
      return { center: undefined, radius: undefined };
    }

    // Calcular centroide
    let sumLng = 0;
    let sumLat = 0;
    const numPoints = coords.length - 1; // Excluir último punto (duplicado)

    for (let i = 0; i < numPoints; i++) {
      sumLng += coords[i][0];
      sumLat += coords[i][1];
    }

    const centerLng = sumLng / numPoints;
    const centerLat = sumLat / numPoints;
    const center: [number, number] = [centerLng, centerLat];

    // Calcular radio promedio (distancia desde centro a cada vértice)
    let sumDistance = 0;

    for (let i = 0; i < numPoints; i++) {
      const distance = this.haversineDistance(center, [
        coords[i][0],
        coords[i][1],
      ]);
      sumDistance += distance;
    }

    const radius = sumDistance / numPoints;

    return { center, radius };
  }

  /**
   * Calcula la distancia entre dos puntos usando la fórmula de Haversine
   *
   * @param coord1 - [lng, lat]
   * @param coord2 - [lng, lat]
   * @returns Distancia en metros
   */
  private haversineDistance(
    coord1: [number, number],
    coord2: [number, number],
  ): number {
    const R = 6371000; // Radio de la Tierra en metros
    const lat1 = (coord1[1] * Math.PI) / 180;
    const lat2 = (coord2[1] * Math.PI) / 180;
    const deltaLat = ((coord2[1] - coord1[1]) * Math.PI) / 180;
    const deltaLng = ((coord2[0] - coord1[0]) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Agrega listeners para el dibujo interactivo de círculos
   * Primer click: establece el centro
   * Movimiento del mouse: actualiza el radio
   * Segundo click: finaliza el círculo
   */
  private addCircleDrawListeners(): void {
    if (!this.map) return;

    const map = this.map;
    const self = this;
    let isDrawing = false;

    const onClick = (e: mapboxgl.MapMouseEvent) => {
      if (!self.drawingCircle) return;

      if (!self.tempCircleCenter) {
        // Primer click: establecer centro
        self.tempCircleCenter = [e.lngLat.lng, e.lngLat.lat];
        isDrawing = true;

        // Agregar marcador temporal en el centro
        if (self.tempMarker) {
          self.tempMarker.remove();
        }
        self.tempMarker = new mapboxgl.Marker({
          color: self.color || '#ffcc33',
        })
          .setLngLat(self.tempCircleCenter)
          .addTo(map);
      } else {
        // Segundo click: finalizar círculo
        const currentPos: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const radius = self.haversineDistance(
          self.tempCircleCenter,
          currentPos,
        );

        if (radius > 10) {
          // Radio mínimo de 10 metros
          const circle: Circle = {
            center: self.tempCircleCenter,
            radius: radius,
          };

          // Dibujar el círculo final
          self.drawCircleFromInput(circle);

          // Emitir el cambio
          self.circle = circle;
          self.ngZone.run(() => {
            self.circleChange.emit(circle);
          });

          // Limpiar estado temporal
          if (self.tempMarker) {
            self.tempMarker.remove();
            self.tempMarker = undefined;
          }
          self.tempCircleCenter = undefined;
          isDrawing = false;

          // Salir del modo de dibujo
          self.parar();
        }
      }
    };

    const onMove = (e: mapboxgl.MapMouseEvent) => {
      if (!self.drawingCircle || !isDrawing || !self.tempCircleCenter) return;

      const currentPos: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      const radius = self.haversineDistance(self.tempCircleCenter, currentPos);

      // Dibujar círculo temporal
      const tempCircle: Circle = {
        center: self.tempCircleCenter,
        radius: radius,
      };

      // Limpiar y redibujar
      self.draw?.deleteAll();
      const polygon = self.circleToPolygon(
        tempCircle.center!,
        tempCircle.radius!,
      );
      const feature = {
        type: 'Feature' as const,
        properties: {},
        geometry: polygon,
      };
      self.draw?.add(feature);
    };

    // Guardar referencias para poder removerlos después
    (map as any)._circleDrawClick = onClick;
    (map as any)._circleDrawMove = onMove;

    map.on('click', onClick);
    map.on('mousemove', onMove);
  }

  /**
   * Remueve los listeners del modo de dibujo
   */
  private removeCircleDrawListeners(): void {
    if (!this.map) return;

    const map = this.map;
    const onClick = (map as any)._circleDrawClick;
    const onMove = (map as any)._circleDrawMove;

    if (onClick) {
      map.off('click', onClick);
      delete (map as any)._circleDrawClick;
    }

    if (onMove) {
      map.off('mousemove', onMove);
      delete (map as any)._circleDrawMove;
    }
  }
}
